import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Avatar,
  Snackbar,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Fab,
  Tooltip,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Group as GroupIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Add as AddIcon,
  ExitToApp as LeaveIcon,
} from '@mui/icons-material';
import api from '../utils/authUtils';
import { useAuth } from '../hooks/useAuth';
import {
  PrayerCard,
  ShareDialog,
  PrayerDetailDialog,
  LeaveCircleDialog,
  PrayerListSkeleton,
  NoCirclePrayers,
} from './common';

function CircleDetail() {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  // State
  const [circle, setCircle] = useState(null);
  const [prayers, setPrayers] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prayersLoading, setPrayersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createPrayerOpen, setCreatePrayerOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedPrayerForShare, setSelectedPrayerForShare] = useState(null);

  // Prayer detail dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);

  // New prayer form state
  const [newPrayer, setNewPrayer] = useState({
    content: '',
    category: 'General',
    is_anonymous: false,
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch circle details
  const fetchCircleDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/circles/${circleId}`);
      setCircle(response.data.circle);
      setError(null);
    } catch (err) {
      console.error('Error fetching circle:', err);
      if (err.response?.status === 404) {
        setError('Circle not found');
      } else if (err.response?.status === 403) {
        setError('You do not have access to this circle');
      } else {
        setError('Failed to load circle details');
      }
    } finally {
      setLoading(false);
    }
  }, [circleId]);

  // Fetch circle prayers
  const fetchPrayers = useCallback(async () => {
    try {
      setPrayersLoading(true);
      const response = await api.get(`/circles/${circleId}/prayers?limit=50`);
      setPrayers(response.data.prayers || []);
    } catch (err) {
      console.error('Error fetching prayers:', err);
      if (err.response?.status === 403) {
        showSnackbar('You must be an approved member to view prayers', 'warning');
      }
    } finally {
      setPrayersLoading(false);
    }
  }, [circleId]);

  // Fetch circle members
  const fetchMembers = useCallback(async () => {
    try {
      const response = await api.get(`/circles/${circleId}/members`);
      setMembers(response.data.members || []);
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  }, [circleId]);

  useEffect(() => {
    fetchCircleDetails();
    fetchPrayers();
    fetchMembers();
  }, [fetchCircleDetails, fetchPrayers, fetchMembers]);

  // Create prayer handler
  const handleCreatePrayer = async () => {
    if (!newPrayer.content.trim()) {
      showSnackbar('Prayer content is required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/prayers', {
        content: newPrayer.content,
        category: newPrayer.category,
        is_anonymous: newPrayer.is_anonymous,
        circle_id: parseInt(circleId),
      });

      showSnackbar('Prayer created successfully!', 'success');
      setCreatePrayerOpen(false);
      setNewPrayer({ content: '', category: 'General', is_anonymous: false });
      fetchPrayers(); // Refresh prayers
    } catch (err) {
      console.error('Error creating prayer:', err);
      showSnackbar(err.response?.data?.message || 'Failed to create prayer', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Leave circle handler
  const handleLeaveCircle = async () => {
    try {
      await api.post(`/circles/${circleId}/leave`);
      showSnackbar('Left circle successfully', 'success');
      setLeaveDialogOpen(false);
      navigate('/circles');
    } catch (err) {
      console.error('Error leaving circle:', err);
      showSnackbar(err.response?.data?.error || 'Failed to leave circle', 'error');
    }
  };

  // Reactions handler
  const handleReact = async (prayer, reactionType) => {
    try {
      if (reactionType === null) {
        // Remove reaction
        await api.delete(`/prayers/${prayer.id}/react`);
      } else {
        // Add/update reaction
        await api.post(`/prayers/${prayer.id}/react`, { reaction_type: reactionType });
      }
      // Refresh prayer data
      fetchPrayers();
    } catch (err) {
      console.error('Error reacting to prayer:', err);
      showSnackbar('Failed to save reaction', 'error');
    }
  };

  // Share handler
  const handleShare = (prayer) => {
    setSelectedPrayerForShare(prayer);
    setShareDialogOpen(true);
  };

  // Comment handler - open detail dialog
  const handleComment = async (prayer) => {
    setSelectedPrayer(prayer);
    setDetailDialogOpen(true);
    loadComments(prayer.id);
  };

  // Load comments for a prayer
  const loadComments = async (prayerId) => {
    setCommentsLoading(true);
    try {
      const response = await api.get(`/prayers/${prayerId}/comments`);
      setComments(response.data.comments || []);
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  };

  // Add comment handler
  const handleAddComment = async (commentData) => {
    try {
      await api.post(`/prayers/${commentData.prayer_id}/comments`, {
        content: commentData.content,
        parent_id: commentData.parent_id,
      });
      loadComments(commentData.prayer_id);
      showSnackbar('Comment added', 'success');
    } catch (err) {
      console.error('Error adding comment:', err);
      showSnackbar('Failed to add comment', 'error');
      throw err;
    }
  };

  // Delete comment handler
  const handleDeleteComment = async (comment) => {
    try {
      await api.delete(`/prayers/comments/${comment.id}`);
      loadComments(selectedPrayer.id);
      showSnackbar('Comment deleted', 'success');
    } catch (err) {
      console.error('Error deleting comment:', err);
      showSnackbar('Failed to delete comment', 'error');
    }
  };

  // Collect prayer handler
  const handleCollect = async (prayer) => {
    try {
      // Get user location
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      await api.post(`/prayers/${prayer.id}/collect`, {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
      showSnackbar('Prayer collected!', 'success');
      fetchPrayers();
    } catch (err) {
      console.error('Error collecting prayer:', err);
      showSnackbar('Failed to collect prayer', 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={200} height={40} />
        </Box>
        <Paper sx={{ p: 3 }}>
          <Skeleton variant="text" width="60%" height={50} />
          <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/circles')}
            sx={{ mt: 2 }}
          >
            Back to Circles
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 10, pb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/circles')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" fontWeight="bold">
              {circle?.name}
            </Typography>
            {circle?.is_private ? (
              <LockIcon color="action" />
            ) : (
              <PublicIcon color="action" />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {circle?.description || 'No description'}
          </Typography>
        </Box>
      </Box>

      {/* Circle Info Bar */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        }}
      >
        <Chip
          icon={<GroupIcon />}
          label={`${circle?.member_count || 0} members`}
          color="primary"
          variant="outlined"
          onClick={() => setMembersDialogOpen(true)}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          label={`${prayers.length} prayers`}
          color="secondary"
          variant="outlined"
        />
        {circle?.requires_approval && (
          <Chip label="Requires Approval" size="small" color="warning" />
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="outlined"
          color="warning"
          startIcon={<LeaveIcon />}
          onClick={() => setLeaveDialogOpen(true)}
          size="small"
        >
          Leave
        </Button>
      </Paper>

      {/* Prayers Grid */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Circle Prayers
        </Typography>

        {prayersLoading ? (
          <PrayerListSkeleton count={4} />
        ) : prayers.length === 0 ? (
          <NoCirclePrayers
            onAction={() => setCreatePrayerOpen(true)}
            actionLabel="Write a Prayer"
          />
        ) : (
          <Grid container spacing={2}>
            {prayers.map((prayer) => (
              <Grid item xs={12} sm={6} md={4} key={prayer.id}>
                <PrayerCard
                  prayer={prayer}
                  onReact={handleReact}
                  onCollect={handleCollect}
                  onComment={handleComment}
                  onShare={handleShare}
                  collected={prayer.is_collected}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Floating Action Button */}
      <Tooltip title="Write a Prayer" placement="left">
        <Fab
          color="primary"
          aria-label="write prayer"
          onClick={() => setCreatePrayerOpen(true)}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 80 : 24,
            right: 24,
            zIndex: 1000,
          }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      {/* Create Prayer Dialog */}
      <Dialog
        open={createPrayerOpen}
        onClose={() => setCreatePrayerOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <AddIcon fontSize="small" />
            </Avatar>
            Write a Prayer for {circle?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="What's on your heart?"
              fullWidth
              multiline
              rows={4}
              value={newPrayer.content}
              onChange={(e) => setNewPrayer({ ...newPrayer, content: e.target.value })}
              placeholder="Share your prayer request with this circle..."
              disabled={submitting}
            />

            <TextField
              select
              label="Category"
              value={newPrayer.category}
              onChange={(e) => setNewPrayer({ ...newPrayer, category: e.target.value })}
              fullWidth
              disabled={submitting}
            >
              <MenuItem value="General">General</MenuItem>
              <MenuItem value="Health">Health</MenuItem>
              <MenuItem value="Family">Family</MenuItem>
              <MenuItem value="Work">Work</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>

            <FormControlLabel
              control={
                <Checkbox
                  checked={newPrayer.is_anonymous}
                  onChange={(e) => setNewPrayer({ ...newPrayer, is_anonymous: e.target.checked })}
                  disabled={submitting}
                />
              }
              label="Post anonymously"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreatePrayerOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleCreatePrayer}
            variant="contained"
            disabled={submitting || !newPrayer.content.trim()}
          >
            {submitting ? <CircularProgress size={24} /> : 'Post Prayer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members Dialog */}
      <Dialog
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon color="primary" />
            Circle Members ({members.length})
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {members.map((member) => (
              <ListItem key={member.id} disablePadding sx={{ py: 1 }}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {member.username?.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={member.full_name || member.username}
                  secondary={member.username}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Leave Circle Dialog */}
      <LeaveCircleDialog
        open={leaveDialogOpen}
        onClose={() => setLeaveDialogOpen(false)}
        onConfirm={handleLeaveCircle}
        circleName={circle?.name}
      />

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        prayer={selectedPrayerForShare}
      />

      {/* Prayer Detail Dialog */}
      <PrayerDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        prayer={selectedPrayer}
        currentUserId={user?.id}
        comments={comments}
        commentsLoading={commentsLoading}
        onReact={handleReact}
        onCollect={handleCollect}
        onAddComment={handleAddComment}
        onDeleteComment={handleDeleteComment}
        onLoadComments={loadComments}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default CircleDetail;
